// =====================================================================
//  The Random Deck button.
//
//  It builds a deck the way a player would, with the dice doing the choosing:
//    - a theme first: one or two affiliations (the Emperor's is the first) and one or two archetypes;
//    - cards of those affiliations, and cards that lean toward those archetypes, are far likelier picks;
//    - a sensible shape: so many Troops, Spells, Buildings, Biomes, Tomes and Triggers, nudged by the
//      archetype (a Siege deck takes more Buildings, a Counter deck more Triggers);
//    - cards come in lumps: three or four copies of a Common, a single copy of a Legendary;
//    - no rarity takes over the deck;
//    - a sensible cost curve: mostly cheap and medium cards, a few heavy ones (more cheap ones for Aggro or
//      Cycle, more heavy ones for Beatdown or Siege).
//  Cards already in the builder are kept, with the copies they were given: it fills the open slots
//  around them with other cards, and reads its theme from them.
//
//  Everything it weighs is in RANDOM_DECK below.
// =====================================================================
import { AFFILIATIONS, ARCHETYPE_IDS, isEmperor, isSecretRare, titleCase, type CardDb, type CardDef } from './cards.ts';
import { MAIN_DECK_SIZE, type Deck } from './deck.ts';
import { bandOf, leaningsOf } from './archetypes.ts';
import { kindOf } from './deckAdvice.ts';

type Range = [number, number];
export const RANDOM_DECK = {
  // How many cards of each type a deck aims for. Whatever is left of the 60 goes to Troops and Spells
  // (restSplit of it to Troops, the rest to Spells), the same way the Deck Helper's aims are worked out.
  types: { troop: 28, building: 3, spell: 15, biome: 2, tome: 2, trigger: 4 } as Record<string, number>,
  // What an archetype changes about that (the deck's second archetype counts for half).
  archetypeTypes: {
    aggro: { troop: 5, spell: -3, building: -1 },
    tempo: { troop: 2, spell: 1, building: -1 },
    control: { spell: 3, troop: -2 },
    beatdown: { troop: 3, spell: -2 },
    cycle: { troop: 2, spell: 1 },
    bait: { troop: 4, spell: -2 },
    combo: { spell: 2, tome: 1, troop: -2 },
    siege: { building: 6, troop: -5 },
    support: { spell: 4, biome: 1, tome: 2, troop: -6 },
    counter: { trigger: 4, spell: 2, troop: -5 },
    brainrot: { trigger: 2, spell: 2, troop: -3 },
    mastermind: { spell: 2, tome: 1, biome: 1, troop: -4 },
    synergy: { spell: 1, troop: -1 },
    midrange: {},
    ramp: { spell: 1, biome: 1, troop: -2 },
    swarm: { troop: 5, spell: -3, building: -1 },
    direct_attack: { spell: 6, troop: -5 },
    mill: { spell: 3, trigger: 1, troop: -4 },
    stall: { building: 3, spell: 2, troop: -4 },
    prison: { spell: 3, trigger: 2, troop: -5 },
    sacrifice: { troop: 2, spell: -1 },
    voltron: { spell: 3, tome: 1, troop: -4 },
    toolbox: { spell: 1, trigger: 1, troop: -2 },
    disruption: { spell: 3, trigger: 2, troop: -5 },
    token: { troop: 3, spell: -1, building: -1 },
  } as Record<string, Record<string, number>>,
  // The cost curve it aims for: the share of the deck that is cheap, medium and heavy (heavy and extra heavy
  // together; the bands are COST_BANDS in archetypes.ts). A card in a band that is short is likelier to be
  // picked, one in a band that is already full far less likely.
  curve: { cheap: 0.35, medium: 0.45, heavy: 0.2 } as Record<string, number>,
  archetypeCurve: {
    aggro: { cheap: 0.55, medium: 0.35, heavy: 0.1 },
    cycle: { cheap: 0.6, medium: 0.32, heavy: 0.08 },
    bait: { cheap: 0.55, medium: 0.35, heavy: 0.1 },
    tempo: { cheap: 0.45, medium: 0.4, heavy: 0.15 },
    beatdown: { cheap: 0.25, medium: 0.4, heavy: 0.35 },
    siege: { cheap: 0.25, medium: 0.45, heavy: 0.3 },
    mastermind: { cheap: 0.28, medium: 0.47, heavy: 0.25 },
    midrange: { cheap: 0.3, medium: 0.55, heavy: 0.15 },
    ramp: { cheap: 0.3, medium: 0.3, heavy: 0.4 },
    swarm: { cheap: 0.6, medium: 0.32, heavy: 0.08 },
    stall: { cheap: 0.3, medium: 0.45, heavy: 0.25 },
    token: { cheap: 0.5, medium: 0.38, heavy: 0.12 },
  } as Record<string, Record<string, number>>,
  restSplit: 0.6,
  curvePull: 1.5, // how much likelier a card in a short band is (at its shortest)
  curveFull: 0.3, // how likely a card in a band that is already full is, compared with others
  // (a band that is full is left out altogether while there are other cards of the type to pick, and once the
  //  open slots are only just enough for the bands that are short, only cards from those bands are picked:
  //  so a finished deck lands on its curve, as the Deck Helper reads it)
  jitter: { troop: 2, spell: 2, building: 1, biome: 1, tome: 1, trigger: 1 } as Record<string, number>, // each target moves up or down by as much as this, so decks differ in shape
  typeMost: { building: 10, biome: 3, tome: 4, trigger: 8 } as Record<string, number>, // never more than this of a type, whatever the archetype
  typeLeast: { biome: 1, trigger: 1 } as Record<string, number>, // …and never fewer
  // Copies taken of a card once it is picked, by rarity: [fewest, most]. (Its own limit still applies.)
  copies: {
    common: [3, 4], uncommon: [2, 4], rare: [2, 3], 'ultra-rare': [1, 3], epic: [1, 2], mythic: [1, 2], legendary: [1, 1], grand: [1, 1], ancient: [1, 1],
  } as Record<string, Range>,
  supportCopies: [1, 2] as Range, // Biomes, Tomes and Triggers, whatever their rarity
  otherCopies: [1, 2] as Range, // cards with no rarity filled in
  // The most cards of one rarity a random deck holds.
  rarityCap: { common: 18, uncommon: 14, rare: 14, 'ultra-rare': 12, epic: 8, mythic: 6, legendary: 4, grand: 3, ancient: 2 } as Record<string, number>,
  // How much likelier a card is to be picked…
  weight: {
    primary: 8, // …in the deck's first affiliation
    secondary: 4, // …in its second
    neutral: 2, // …Neutral (fits anywhere)
    unassigned: 1.2, // …no affiliation filled in
    other: 0.25, // …any other affiliation: a splash now and then
    named: 5, // …no affiliation, but named after one of the deck's ("Duned Trigger" in a Duned deck)
    archetype: 3.5, // …leaning toward the deck's first archetype
    secondArchetype: 2, // …or its second
    supportFocus: 1.5, // Biomes, Tomes and Triggers follow the deck's affiliations more strictly still (a power: 1 = like any card)
  },
  // …and by rarity: a common is picked a little more readily than an ancient
  rarityWeight: { common: 1.4, uncommon: 1.25, rare: 1.1, 'ultra-rare': 1, epic: 0.9, mythic: 0.75, legendary: 0.6, grand: 0.5, ancient: 0.4 } as Record<string, number>,
  twoAffiliations: 0.7, // how often a deck gets a second affiliation (otherwise one, plus Neutral)
  twoArchetypes: 0.5, // how often it gets a second archetype
};

export interface RandomDeckTheme {
  affiliations: string[]; // first one is the main one
  archetypes: string[];
}
export interface RandomDeckResult {
  deck: Deck;
  theme: RandomDeckTheme;
  added: number; // main-deck cards put in
  kept: number; // main-deck cards that were already there
  emperorPicked: boolean;
}

const SUPPORT_TYPES = ['biome', 'tome', 'trigger'];
const FILL_ORDER = ['biome', 'tome', 'trigger', 'building', 'spell', 'troop'];

/**
 * Fills a deck at random (see the top of this file). `base` is what is already in the builder: it is
 * kept, and only the open slots are filled. Returns null if cards.csv can't fill a deck.
 */
export function buildRandomDeck(db: CardDb, rng: () => number = Math.random, base: Deck = new Map()): RandomDeckResult | null {
  const R = RANDOM_DECK;
  const deck: Deck = new Map(base);
  const pick = <T>(list: T[]) => list[Math.floor(rng() * list.length)];
  const between = ([lo, hi]: Range) => lo + Math.floor(rng() * (hi - lo + 1));
  const real = (a: string) => (AFFILIATIONS as readonly string[]).includes(a);

  // rows that haven't been filled in yet (no type) are left out, as long as the finished cards can fill a deck
  const all = db.list.filter((c) => !isEmperor(c) && !isSecretRare(c)); // (the secret rares are never picked at random)
  const done = all.filter((c) => !c.types.includes('unassigned'));
  const pool = done.reduce((sum, c) => sum + c.limit, 0) >= MAIN_DECK_SIZE ? done : all;
  const emperors = db.list.filter(isEmperor);
  if (pool.reduce((sum, c) => sum + c.limit, 0) < MAIN_DECK_SIZE) return null;

  // ---- what is already there
  const mainCards = () => [...deck].map(([num, copies]) => ({ card: db.byNumber.get(num), copies })).filter((e): e is { card: CardDef; copies: number } => !!e.card && !isEmperor(e.card));
  const mainCount = () => mainCards().reduce((n, e) => n + e.copies, 0);
  const kept = mainCount();
  const tally = (keys: (c: CardDef) => string[]) => {
    const t = new Map<string, number>();
    for (const { card, copies } of mainCards()) for (const k of keys(card)) t.set(k, (t.get(k) ?? 0) + copies);
    return [...t].sort((a, b) => b[1] - a[1]);
  };

  // ---- the Emperor, and the theme
  const haveAff = tally((c) => c.affiliations.filter((a) => real(a) && a !== 'neutral'));
  let emperor = [...deck.keys()].map((n) => db.byNumber.get(n)).find((c) => !!c && isEmperor(c));
  const emperorPicked = !emperor;
  if (!emperor) {
    if (!emperors.length) return null;
    // an Emperor that matches the cards already chosen, if there is one
    const fitting = haveAff.length && haveAff[0][1] >= 4 ? emperors.filter((e) => e.affiliations.includes(haveAff[0][0])) : [];
    emperor = pick(fitting.length ? fitting : emperors);
    deck.set(emperor.number, 1);
  }
  const emperorAff = emperor.affiliations.find(real);
  const others = (not: string[]) => (AFFILIATIONS as readonly string[]).filter((a) => a !== 'neutral' && !not.includes(a));
  const primary = haveAff.length && haveAff[0][1] >= 4 ? haveAff[0][0] : (emperorAff ?? pick(others([])));
  let secondary: string | undefined = haveAff.find(([a, n]) => a !== primary && n >= 3)?.[0];
  if (!secondary && haveAff.length && haveAff[0][1] >= 4 && emperorAff && emperorAff !== primary && emperorAff !== 'neutral') secondary = emperorAff;
  if (!secondary && rng() < R.twoAffiliations) secondary = pick(others([primary]));

  const haveLean = tally((c) => [...leaningsOf(c).keys()]);
  const archetype = haveLean.length && haveLean[0][1] >= 4 ? haveLean[0][0] : pick([...ARCHETYPE_IDS]);
  let second: string | undefined = haveLean.find(([a, n]) => a !== archetype && n >= 4)?.[0];
  if (!second && rng() < R.twoArchetypes) second = pick(ARCHETYPE_IDS.filter((a) => a !== archetype));

  // ---- the shape of the deck
  const target: Record<string, number> = {};
  for (const type of FILL_ORDER) {
    const nudge = (R.archetypeTypes[archetype]?.[type] ?? 0) + (second ? (R.archetypeTypes[second]?.[type] ?? 0) / 2 : 0);
    const aim = Math.round(R.types[type] + nudge + (rng() * 2 - 1) * (R.jitter[type] ?? 1));
    target[type] = Math.max(R.typeLeast[type] ?? 0, Math.min(R.typeMost[type] ?? Infinity, aim));
  }
  // whatever is left of the 60 is shared out between Troops and Spells now, so they are aimed at too
  const rest = MAIN_DECK_SIZE - FILL_ORDER.reduce((n, t) => n + target[t], 0);
  if (rest > 0) {
    target.troop += Math.round(rest * R.restSplit);
    target.spell += rest - Math.round(rest * R.restSplit);
  }

  // ---- the cost curve it aims for
  const curve = R.archetypeCurve[archetype] ?? R.curve;
  const bandKey = (c: CardDef) => (c.cost === null ? null : bandOf(c.cost).id === 'cheap' ? 'cheap' : bandOf(c.cost).id === 'medium' ? 'medium' : 'heavy');
  const bandHave = (band: string) => mainCards().reduce((n, e) => n + (bandKey(e.card) === band ? e.copies : 0), 0);
  const bandWant = (band: string) => Math.round((curve[band] ?? 0) * MAIN_DECK_SIZE);
  const curveWeight = (c: CardDef) => {
    const band = bandKey(c);
    if (!band) return 1; // no cost yet: nothing to judge by
    const have = bandHave(band);
    const want = bandWant(band);
    if (have >= want) return R.curveFull;
    return 1 + R.curvePull * ((want - have) / Math.max(1, want));
  };
  /** Narrows the picks to keep the curve on course: only the short bands once the open slots are just enough
   *  for them, and never a band that is already full while there is anything else to pick. */
  const onCurve = (candidates: CardDef[], types: string[] | null, capped: boolean): CardDef[] => {
    const short = (['cheap', 'medium', 'heavy'] as const).map((b) => [b, bandWant(b) - bandHave(b)] as const).filter(([, n]) => n > 0);
    const shortBy = short.reduce((n, [, d]) => n + d, 0);
    if (short.length && shortBy >= MAIN_DECK_SIZE - main) {
      const inShort = (c: CardDef) => short.some(([b]) => b === bandKey(c));
      const only = candidates.filter(inShort);
      if (only.length) return only;
      // nothing new in those bands (heavy cards are few, and mostly rare): more copies of one already taken
      const more = open(types, capped, true).filter(inShort);
      if (more.length) return more;
    }
    const notFull = candidates.filter((c) => { const b = bandKey(c); return !b || bandHave(b) < bandWant(b); });
    return notFull.length ? notFull : candidates;
  };

  // ---- how likely each card is
  const weightOf = (c: CardDef) => {
    const W = R.weight;
    const affs = c.affiliations.filter(real);
    let w: number;
    if (affs.includes(primary)) w = W.primary;
    else if (secondary && affs.includes(secondary)) w = W.secondary;
    else if (affs.includes('neutral')) w = W.neutral;
    else if (!affs.length) {
      // no affiliation: a card named after one belongs with it ("Duned Trigger")
      const name = c.name.toLowerCase();
      const named = (AFFILIATIONS as readonly string[]).filter((a) => name.includes(a));
      w = named.includes(primary) ? W.named : secondary && named.includes(secondary) ? W.named * 0.6 : named.length ? W.other : W.unassigned;
    } else w = W.other;
    if (c.types.some((t) => SUPPORT_TYPES.includes(t))) w = w ** W.supportFocus;
    const leans = leaningsOf(c);
    if (leans.has(archetype)) w *= W.archetype;
    else if (second && leans.has(second)) w *= W.secondArchetype;
    return w * (R.rarityWeight[c.rarity] ?? 1) * curveWeight(c);
  };

  // ---- filling
  const rarityCount = new Map<string, number>();
  for (const { card, copies } of mainCards()) rarityCount.set(card.rarity, (rarityCount.get(card.rarity) ?? 0) + copies);
  const rarityRoom = (c: CardDef) => (R.rarityCap[c.rarity] ?? Infinity) - (rarityCount.get(c.rarity) ?? 0);
  const roomFor = (c: CardDef) => c.limit - (deck.get(c.number) ?? 0);
  // a card is counted once, as the Deck Helper counts it: a Troop that is also a Building is a Troop
  const isType = (c: CardDef, type: string) => (SUPPORT_TYPES.includes(type) ? kindOf(c) === 'support' && c.types.includes(type) : kindOf(c) === type);
  const typeCount = (type: string) => mainCards().filter((e) => isType(e.card, type)).reduce((n, e) => n + e.copies, 0);
  // a card already in the builder keeps the number of copies it was given: only other cards are added
  const picked = new Set<number>(base.keys());
  let main = kept;

  const take = (all: CardDef[], atMost: number, capped: boolean, types: string[] | null) => {
    const candidates = onCurve(all, types, capped);
    const weights = candidates.map(weightOf);
    let roll = rng() * weights.reduce((a, b) => a + b, 0);
    let card = candidates[candidates.length - 1];
    for (let i = 0; i < candidates.length; i++) {
      roll -= weights[i];
      if (roll < 0) { card = candidates[i]; break; }
    }
    const lump = between(card.types.some((t) => SUPPORT_TYPES.includes(t)) ? R.supportCopies : (R.copies[card.rarity] ?? R.otherCopies));
    // (a lump never runs its cost band past the curve: four copies of a cheap card when the deck wants one more
    //  cheap card would leave the heavy end short)
    const band = bandKey(card);
    const bandRoom = band && bandHave(band) < bandWant(band) ? bandWant(band) - bandHave(band) : Infinity;
    const n = Math.max(1, Math.min(lump, roomFor(card), MAIN_DECK_SIZE - main, atMost, bandRoom, capped ? rarityRoom(card) : Infinity));
    deck.set(card.number, (deck.get(card.number) ?? 0) + n);
    rarityCount.set(card.rarity, (rarityCount.get(card.rarity) ?? 0) + n);
    picked.add(card.number);
    main += n;
  };
  // `more`: cards Random Deck already took (never the ones that were in the builder), up to the most copies
  // their rarity takes
  const mostCopies = (c: CardDef) => (c.types.some((t) => SUPPORT_TYPES.includes(t)) ? R.supportCopies : (R.copies[c.rarity] ?? R.otherCopies))[1];
  const open = (types: string[] | null, capped: boolean, more = false): CardDef[] =>
    pool.filter((c) => (more ? picked.has(c.number) && !base.has(c.number) && (deck.get(c.number) ?? 0) < mostCopies(c) : !picked.has(c.number)) && roomFor(c) > 0 && (!capped || rarityRoom(c) > 0) && (!types || types.some((t) => isType(c, t))));

  for (const type of FILL_ORDER) {
    while (main < MAIN_DECK_SIZE) {
      const need = target[type] - typeCount(type);
      const candidates = need > 0 ? open([type], true) : [];
      if (!candidates.length) break;
      take(candidates, need, true, [type]);
    }
  }
  // whatever is left: Troops and Spells first, then anything that fits
  for (const [types, capped] of [[['troop', 'spell'], true], [null, true], [null, false]] as [string[] | null, boolean][]) {
    while (main < MAIN_DECK_SIZE) {
      const candidates = open(types, capped);
      if (!candidates.length) break;
      take(candidates, Infinity, capped, types);
    }
  }
  if (main < MAIN_DECK_SIZE) return null;
  return { deck, theme: { affiliations: secondary ? [primary, secondary] : [primary], archetypes: second ? [archetype, second] : [archetype] }, added: main - kept, kept, emperorPicked };
}

/** A random legal deck — handy for testing the table. */
export function randomDeck(db: CardDb, rng: () => number = Math.random): Deck | null {
  return buildRandomDeck(db, rng)?.deck ?? null;
}

/** "Duned and Undead, leaning Aggro": a theme in words. */
export function themeText(theme: RandomDeckTheme, archetypeName: (id: string) => string = titleCase): string {
  return `${theme.affiliations.map(titleCase).join(' and ')}, leaning ${theme.archetypes.map(archetypeName).join(' and ')}`;
}
